import { ArrowLongRightIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/solid'
import classNames from 'classnames'
import { Dispatch, ReactNode, SetStateAction } from 'react'
import styled from 'styled-components'

import { Language } from '../../../constants'
import { LanguagesListBox } from '../../UI/LanguagesListBox'
import { TemplatesListBox } from '../../UI/TemplatesListBox'
import { Toggle } from '../../UI/Toggle'
import { Tooltip } from '../../UI/Tooltip'

interface Props {
    customTemplateId: string | undefined
    templateId: string | undefined
    isTranslationEnabled: boolean
    languageSource: Language | undefined
    languageTarget: Language
    onSetLanguageTarget: (language: Language) => void
    setWhisperTranscriber: () => void
    setAzureTranscriber: () => void
    isWhisperTranscriber: boolean
    setTemplateId: (_templateId: string, isCustomTemplate: boolean) => void
    setlanguageSource: Dispatch<SetStateAction<Language | undefined>>
    setlanguageTarget: (value: SetStateAction<Language>) => void
    setIsTranslationEnabled: Dispatch<SetStateAction<boolean>>
}

export const ConfigNewVisit = ({
    setIsTranslationEnabled,
    isWhisperTranscriber,
    templateId,
    customTemplateId,
    setTemplateId,
    setlanguageTarget,
    setlanguageSource,
    setAzureTranscriber,
    setWhisperTranscriber,
    isTranslationEnabled,
    languageSource,
    languageTarget,
    onSetLanguageTarget
}: Props) => {
    return (
        <div className="bg-gray-50 rounded-md border">
            <OptionWrapper className="border-b h-16" labelNode="Template" rightNode={<TemplatesListBox templateId={customTemplateId ?? templateId} onChange={setTemplateId} />} />

            {isTranslationEnabled ? (
                <>
                    <OptionWrapper
                        className="border-b h-16"
                        labelNode="Languages"
                        rightNode={
                            <div className="flex items-center">
                                <LanguagesListBox value={languageSource} onChange={setlanguageSource} />
                                <ArrowLongRightIcon className="h-5 w-5 mx-1" />
                                <LanguagesListBox value={languageTarget} onChange={setlanguageTarget} />
                            </div>
                        }
                    />
                </>
            ) : (
                <OptionWrapper
                    className="border-b h-16"
                    labelNode="Language"
                    tooltipContent="The language you and the patient will be speaking during the visit"
                    rightNode={<LanguagesListBox value={languageTarget} onChange={onSetLanguageTarget} />}
                />
            )}

            <OptionWrapper
                className="h-16"
                labelNode={
                    <div className="flex items-center">
                        <div>Real-time translation</div>
                        <div className="rounded-md bg-blue-50 px-2 py-1 text-xs font-semibold text-primary ring-1 ring-inset ring-primary ml-3">Beta</div>
                    </div>
                }
                rightNode={
                    <Toggle
                        value={isTranslationEnabled}
                        onChange={(value) => {
                            setIsTranslationEnabled(value)

                            // If translation is enabled, then set default language source
                            if (value) {
                                setlanguageSource((prev) => {
                                    if (!prev) {
                                        return languageTarget
                                    }
                                    return prev
                                })
                            }

                            // Else set language source to undefined
                            else {
                                setlanguageSource(undefined)
                            }

                            // If translation is enabled, then switch to Azure STT
                            if (value) {
                                setAzureTranscriber()
                            }

                            // Else switch to Whisper transcriber, if applicable
                            else if (isWhisperTranscriber) {
                                setWhisperTranscriber()
                            }
                        }}
                    />
                }
            />
        </div>
    )
}

const OptionWrapperStyles = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;

    ul {
        width: fit-content;
    }
`

const OptionWrapper = ({ className, tooltipContent, labelNode, rightNode }: { className?: string; tooltipContent?: string; labelNode: ReactNode; rightNode: ReactNode }) => {
    if (tooltipContent) {
        return (
            <OptionWrapperStyles className={classNames('py-3 px-4', className)}>
                <div className="flex items-center">
                    <div className="text-sm font-medium">{labelNode}</div>

                    <Tooltip content={tooltipContent} placement="top">
                        <QuestionMarkCircleIcon className="h-5 w-5 text-gray-500 ml-1" />
                    </Tooltip>
                </div>
                {rightNode}
            </OptionWrapperStyles>
        )
    }

    return (
        <OptionWrapperStyles className={classNames('py-3 px-4', className)}>
            <div className="text-sm font-medium">{labelNode}</div>
            {rightNode}
        </OptionWrapperStyles>
    )
}
